import React, { useState } from "react";
import { Box, Button, Flex, HStack, Icon, Input, Menu, MenuButton, MenuItem, MenuList, Portal, SimpleGrid, Text, VStack, useToast } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { motion, AnimatePresence } from "framer-motion";
import {
  PiToggleLeftDuotone, PiRadioButtonDuotone, PiCheckSquareDuotone, PiCaretDownBold, PiTrashDuotone,
  PiPlusBold, PiScalesDuotone, PiListBulletsDuotone, PiPercentDuotone, PiHashDuotone
} from "react-icons/pi";
import { CustomInputWithCounter } from "@lubbezposrednio-frontend/commons";
import { VotingQuestionArrayType, QuestionType, QuestionThresholdType } from "@lubbezposrednio-frontend/core";
import { CreateVotingViewModel } from "../CreateVotingView/CreateVotingViewModel";
import { drgba, EASE } from "../../components/glassUi";

const TYPE_META: Record<QuestionType, { icon: React.ElementType; label: string; color: string }> = {
  yes_no: { icon: PiToggleLeftDuotone, label: "TAK / NIE", color: "#2563eb" },
  single: { icon: PiRadioButtonDuotone, label: "Jeden wybór", color: "#7b2ff7" },
  multiple: { icon: PiCheckSquareDuotone, label: "Wiele wyborów", color: "#10b981" }
};
const TYPES: QuestionType[] = ["yes_no", "single", "multiple"];

const THRESHOLD_OPTIONS: Record<QuestionType, { value: QuestionThresholdType; label: string }[]> = {
  yes_no: [
    { value: "simple", label: "Zwykła większość" },
    { value: "two_thirds", label: "Większość 2/3" },
    { value: "custom", label: "Własny %" }
  ],
  single: [
    { value: "plurality", label: "Zwykła większość" },
    { value: "custom", label: "Wymagany próg" }
  ],
  multiple: [
    { value: "plurality", label: "Ranking (bez progu)" },
    { value: "custom", label: "Wymagany próg" }
  ]
};

const thresholdSummary = (q: VotingQuestionArrayType): string => {
  const t = q.thresholdType ?? "plurality";
  if (t === "two_thirds") return "Większość 2/3";
  if (t === "custom") return `Próg ${q.thresholdPercent ?? "—"}%`;
  if (t === "plurality" && q.qtype === "multiple") return "Ranking";
  return "Zwykła większość";
};
const answersSummary = (q: VotingQuestionArrayType): string => {
  if (q.qtype === "yes_no") return "Tak / Nie";
  const n = q.answers.filter(a => a.content.trim()).length || q.answers.length;
  return `${n} ${n === 1 ? "odpowiedź" : "odpowiedzi"}`;
};

const Chip: React.FC<{ icon: React.ElementType; children: React.ReactNode; color: string }> = ({ icon, children, color }) => (
  <HStack spacing="5px" px="9px" py="3px" borderRadius="full" bg={drgba(color, 0.1)} flexShrink={0}>
    <Icon as={icon} boxSize="12px" color={color} />
    <Text fontSize="11.5px" fontWeight="700" color={color} whiteSpace="nowrap">{children}</Text>
  </HStack>
);

// ── Pojedyncza, zwijana karta pytania ──
const QuestionCard: React.FC<{ model: CreateVotingViewModel; q: VotingQuestionArrayType; order: number; removable: boolean }> = observer(({ model, q, order, removable }) => {
  const vq = model.votingQuestionsModel;
  const toast = useToast();
  const open = vq.expandedQuestionId === q.id;
  const meta = TYPE_META[q.qtype ?? "single"];

  return (
    <Box borderRadius="16px" bg="rgba(255,255,255,0.6)" border="1.5px solid" borderColor={open ? drgba(meta.color, 0.35) : "rgba(24,12,48,0.08)"} overflow="hidden" transition={`border-color ${EASE}`}>
      {/* Header — klik zwija/rozwija */}
      <Flex as="button" type="button" w="100%" textAlign="left" onClick={() => vq.setExpandedQuestion(q.id)} align="center" gap="11px" p="13px 14px" _hover={{ bg: "rgba(24,12,48,0.02)" }}>
        <Flex w="26px" h="26px" borderRadius="8px" bg={meta.color} color="white" align="center" justify="center" fontWeight="800" fontSize="13px" flexShrink={0}>{order}</Flex>
        <Box flex="1" minW={0}>
          <Text fontSize="14px" fontWeight="800" color={q.question?.trim() ? "#241536" : "#a29bb2"} noOfLines={1}>
            {q.question?.trim() || "Nowe pytanie…"}
          </Text>
          {!open && (
            <HStack spacing="6px" mt="6px" overflowX="hidden">
              <Chip icon={meta.icon} color={meta.color}>{meta.label}</Chip>
              <Chip icon={PiListBulletsDuotone} color="#8a8397">{answersSummary(q)}</Chip>
              <Chip icon={PiScalesDuotone} color="#0d9488">{thresholdSummary(q)}</Chip>
            </HStack>
          )}
        </Box>
        {removable && (
          <Box as="span" onClick={e => { e.stopPropagation(); vq.removeQuestion(q.id); }} display="inline-flex" p="7px" borderRadius="9px" color="#c41a3a" _hover={{ bg: drgba("#e51f44", 0.1) }} flexShrink={0}>
            <Icon as={PiTrashDuotone} boxSize="16px" />
          </Box>
        )}
        <Icon as={PiCaretDownBold} boxSize="12px" color="#b8b2c6" transform={open ? "rotate(180deg)" : undefined} transition={`transform ${EASE}`} flexShrink={0} />
      </Flex>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: "hidden" }}>
            <VStack align="stretch" spacing="14px" p="14px" pt="2px">
              {/* Kompaktowy toolbar pytania: typ · próg · max · wstrzymanie (nad treścią) */}
              <Flex bg="rgba(24,12,48,0.045)" borderRadius="12px" p="6px" gap="6px" wrap="wrap" align="center">
                {/* Typ */}
                <Menu placement="bottom-start" isLazy>
                  <MenuButton as={Button} h="32px" px="10px" borderRadius="9px" variant="ghost" bg="transparent" fontSize="12.5px" fontWeight="800" color="#4a4458"
                    leftIcon={<Icon as={meta.icon} boxSize="15px" color={meta.color} />} rightIcon={<Icon as={PiCaretDownBold} boxSize="9px" color="#b8b2c6" />} _hover={{ bg: "white" }} _active={{ bg: "white" }}>
                    {meta.label}
                  </MenuButton>
                  <Portal>
                    <MenuList minW="210px" zIndex={1600} borderRadius="14px" py="6px" boxShadow="0 20px 50px -20px rgba(43,0,90,0.5)">
                      {TYPES.map(t => { const m = TYPE_META[t]; return (
                        <MenuItem key={t} onClick={() => { vq.setQuestionType(q.id, t); if (t === "multiple" && !q.maxSelections) { const n = q.answers.filter(a => a.content.trim()).length; vq.setMaxSelections(q.id, n >= 2 ? n : 2); } }} icon={<Icon as={m.icon} boxSize="16px" color={m.color} />} fontSize="13.5px" fontWeight="700">{m.label}</MenuItem>
                      ); })}
                    </MenuList>
                  </Portal>
                </Menu>

                <Box w="1px" h="20px" bg="rgba(24,12,48,0.1)" />

                {/* Próg / zasada */}
                <Menu placement="bottom-start" isLazy>
                  <MenuButton as={Button} h="32px" px="10px" borderRadius="9px" variant="ghost" bg="transparent" fontSize="12.5px" fontWeight="800" color="#4a4458"
                    leftIcon={<Icon as={PiScalesDuotone} boxSize="15px" color="#0d9488" />} rightIcon={<Icon as={PiCaretDownBold} boxSize="9px" color="#b8b2c6" />} _hover={{ bg: "white" }} _active={{ bg: "white" }}>
                    {thresholdSummary(q)}
                  </MenuButton>
                  <Portal>
                    <MenuList minW="220px" zIndex={1600} borderRadius="14px" py="6px" boxShadow="0 20px 50px -20px rgba(43,0,90,0.5)">
                      {THRESHOLD_OPTIONS[q.qtype ?? "single"].map(o => (
                        <MenuItem key={o.value} onClick={() => vq.setThreshold(q.id, o.value)} fontSize="13.5px" fontWeight="700">{o.label}</MenuItem>
                      ))}
                    </MenuList>
                  </Portal>
                </Menu>

                {q.thresholdType === "custom" && (
                  <HStack spacing="4px" bg="white" borderRadius="9px" h="32px" px="10px">
                    <Icon as={PiPercentDuotone} boxSize="13px" color="#0d9488" />
                    <Input variant="unstyled" type="number" min={0} max={100} w="40px" value={q.thresholdPercent ?? ""} onChange={e => vq.setThresholdPercent(q.id, e.target.value === "" ? undefined : Number(e.target.value))} placeholder="50" fontSize="12.5px" fontWeight="800" />
                  </HStack>
                )}

                {q.qtype === "multiple" && (
                  <HStack spacing="5px" bg="white" borderRadius="9px" h="32px" px="10px">
                    <Icon as={PiHashDuotone} boxSize="13px" color={meta.color} />
                    <Text fontSize="12px" fontWeight="700" color="#7a748a">Max</Text>
                    <Input variant="unstyled" type="number" min={1} w="34px" value={q.maxSelections ?? ""} onChange={e => vq.setMaxSelections(q.id, e.target.value === "" ? undefined : Number(e.target.value))} placeholder="—" fontSize="12.5px" fontWeight="800" />
                  </HStack>
                )}

                <Box as="button" type="button" onClick={() => vq.toggleAbstain(q.id, !q.allowAbstain)} h="32px" px="12px" borderRadius="9px" fontSize="12.5px" fontWeight="800" transition={`all ${EASE}`} ml="auto"
                  bg={q.allowAbstain ? drgba("#7b2ff7", 0.14) : "white"} color={q.allowAbstain ? "#7b2ff7" : "#7a748a"}>
                  <HStack spacing="6px"><Icon as={PiCheckSquareDuotone} boxSize="14px" /><Text>Wstrzymanie</Text></HStack>
                </Box>
              </Flex>

              {/* Treść pytania */}
              <Box>
                <SubLabel>Treść pytania</SubLabel>
                <CustomInputWithCounter value={q.question} onChange={(v: string) => vq.changeQuestion(q.id, v)} maxLength={500} placeholder="np. Czy przyjmujemy budżet na 2026 r.?" />
              </Box>

              {/* Odpowiedzi */}
              {q.qtype !== "yes_no" ? (
                <Box>
                  <SubLabel>Odpowiedzi</SubLabel>
                  <VStack align="stretch" spacing="8px">
                    {q.answers.map((a, i) => (
                      <HStack key={a.id} spacing="8px">
                        <Flex w="24px" h="24px" borderRadius="7px" bg="rgba(24,12,48,0.06)" align="center" justify="center" flexShrink={0}>
                          <Text fontSize="11px" fontWeight="800" color="#8a8397">{i + 1}</Text>
                        </Flex>
                        <Input value={a.content} onChange={e => vq.changeAnswer(q.id, a.id, e.target.value)} placeholder={`Odpowiedź ${i + 1}`} h="40px" borderRadius="11px" bg="rgba(255,255,255,0.72)" border="1.5px solid rgba(24,12,48,0.1)" fontWeight="600" fontSize="14px" _hover={{ borderColor: drgba(meta.color, 0.4) }} _focusVisible={{ borderColor: meta.color, boxShadow: `0 0 0 3px ${drgba(meta.color, 0.16)}` }} />
                        {q.answers.length > 2 && (
                          <Box as="button" type="button" onClick={() => vq.removeAnswer(q.id, a.id)} p="8px" borderRadius="9px" color="#c41a3a" _hover={{ bg: drgba("#e51f44", 0.1) }} flexShrink={0}><Icon as={PiTrashDuotone} boxSize="15px" /></Box>
                        )}
                      </HStack>
                    ))}
                    <Button size="sm" h="38px" variant="ghost" leftIcon={<Icon as={PiPlusBold} boxSize="12px" />} onClick={() => vq.addAnswer(q.id, toast)} bg={drgba(meta.color, 0.08)} color={meta.color} borderRadius="11px" fontWeight="800" fontSize="13px" _hover={{ bg: drgba(meta.color, 0.16) }} alignSelf="flex-start">Dodaj odpowiedź</Button>
                  </VStack>
                </Box>
              ) : (
                <HStack spacing="8px">
                  <Chip icon={PiToggleLeftDuotone} color="#10b981">Tak</Chip>
                  <Chip icon={PiToggleLeftDuotone} color="#e51f44">Nie</Chip>
                  <Text fontSize="12.5px" color="#a29bb2">odpowiedzi ustawione automatycznie</Text>
                </HStack>
              )}
            </VStack>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
});

const SubLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontSize="11.5px" fontWeight="800" letterSpacing="0.04em" textTransform="uppercase" color="#a29bb2" mb="9px">{children}</Text>
);

export const StepQuestions: React.FC<{ model: CreateVotingViewModel }> = observer(({ model }) => {
  const vq = model.votingQuestionsModel;
  const toast = useToast();
  const questions = vq.questionsArray;
  const filled = questions.filter(q => q.question?.trim()).length;
  const [started, setStarted] = useState(() => filled > 0 || questions.length > 1);

  const startType = (t: QuestionType) => {
    const id = questions[0]?.id;
    if (id != null) {
      vq.setQuestionType(id, t);
      if (t === "multiple") vq.setMaxSelections(id, 2);
      if (vq.expandedQuestionId !== id) vq.setExpandedQuestion(id); // setExpandedQuestion przełącza — rozwiń tylko gdy zwinięte
    }
    setStarted(true);
  };

  // Empty state — wybór typu pierwszego pytania.
  if (!started) {
    return (
      <Flex direction="column" align="center" textAlign="center" gap="16px" border="1.5px dashed rgba(24,12,48,0.14)" borderRadius="16px" bg="rgba(24,12,48,0.015)" p={{ base: "24px", md: "32px" }}>
        <Flex w="52px" h="52px" borderRadius="16px" bg={drgba("#7b2ff7", 0.1)} align="center" justify="center">
          <Icon as={PiListBulletsDuotone} boxSize="26px" color="#7b2ff7" />
        </Flex>
        <Box>
          <Text fontSize="15px" fontWeight="800" color="#241536" letterSpacing="-0.01em">Nie dodano jeszcze pytań</Text>
          <Text fontSize="13px" color="#8a8397" mt="4px" maxW="380px">Zacznij od wyboru typu pierwszego pytania.</Text>
        </Box>
        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="10px" w="100%" maxW="500px">
          {TYPES.map(t => {
            const m = TYPE_META[t];
            return (
              <Flex key={t} as="button" type="button" onClick={() => startType(t)} direction="column" align="center" gap="9px" p="18px 12px" borderRadius="14px"
                bg="rgba(255,255,255,0.7)" border="1.5px solid rgba(24,12,48,0.08)" transition={`all ${EASE}`}
                _hover={{ borderColor: m.color, bg: drgba(m.color, 0.04), transform: "translateY(-2px)", boxShadow: `0 12px 26px -16px ${drgba(m.color, 0.55)}` }} _active={{ transform: "translateY(0)" }}>
                <Flex w="42px" h="42px" borderRadius="13px" bg={drgba(m.color, 0.12)} align="center" justify="center"><Icon as={m.icon} boxSize="22px" color={m.color} /></Flex>
                <Text fontSize="13px" fontWeight="800" color="#241536">{m.label}</Text>
              </Flex>
            );
          })}
        </SimpleGrid>
      </Flex>
    );
  }

  return (
    <VStack align="stretch" spacing="10px">
      {questions.map((q, i) => (
        <QuestionCard key={q.id} model={model} q={q} order={i + 1} removable={questions.length > 1} />
      ))}
      <Button h="44px" variant="ghost" leftIcon={<Icon as={PiPlusBold} boxSize="13px" />} onClick={() => vq.addQuestion(toast)} bg="rgba(24,12,48,0.04)" color="#4a4458" borderRadius="13px" fontWeight="800" fontSize="14px" border="1.5px dashed rgba(24,12,48,0.15)" _hover={{ bg: "white", color: "#7b2ff7", borderColor: drgba("#7b2ff7", 0.4) }}>
        Dodaj pytanie
      </Button>
    </VStack>
  );
});
